import { RotateCcw } from 'lucide-react'
import { useAppEvent } from '../../AppContext.tsx'
import { graphStats } from 'graph-grammar'
import { labelColor } from '../../colors.ts'

export function Stats() {
  // Re-render on graph changes (label set / counts) and on settings changes (a
  // per-label colour override was added or cleared).
  const app = useAppEvent('graph', 'settings')
  const s = graphStats(app.engine.graph)
  const overrides = app.settings.labelColors
  const hidden = new Set(app.settings.hiddenNodes)

  // Replace one label's colour override (from the swatch's colour picker).
  const setColor = (label: string, color: string) => app.setSettings({ labelColors: { ...overrides, [label]: color } })

  // Drop a label's override so it falls back to its automatic (hashed) colour.
  const clearColor = (label: string) => {
    const next = { ...overrides }
    delete next[label]
    app.setSettings({ labelColors: next })
  }

  // Switch every node with this label on/off. Shares app.settings.hiddenNodes
  // with the graph filter popover, so the two stay in sync. The colour swatch is
  // a separate control, so picking a colour never toggles visibility.
  const toggleHidden = (label: string) => {
    const next = new Set(hidden)
    if (next.has(label)) next.delete(label)
    else next.add(label)
    app.setSettings({ hiddenNodes: [...next] })
  }

  return (
    <div className="stats">
      <div className="stat-row">
        <span className="stat big">{s.nodes}</span>
        <span className="stat-label">nodes</span>
        <span className="stat big">{s.edges}</span>
        <span className="stat-label">edges</span>
        <span className="stat big">{app.engine.steps}</span>
        <span className="stat-label">steps</span>
      </div>
      <div className="legend">
        {s.labels.slice(0, 16).map(([label, n]) => {
          const overridden = !!overrides[label]
          const off = hidden.has(label)
          return (
            <span className={'legend-item' + (overridden ? ' overridden' : '') + (off ? ' off' : '')} key={label}>
              {/* The swatch is a colour picker: click it to recolour every node
                  with this label, so same-coloured labels can be told apart. It's
                  a sibling of the toggle, so picking a colour never hides/shows. */}
              <span className="legend-swatch" style={{ background: labelColor(label, overrides) }}>
                <input
                  type="color"
                  className="legend-color"
                  value={labelColor(label, overrides)}
                  aria-label={`Colour for ${label}`}
                  title={`Pick a colour for “${label}”`}
                  onChange={(e) => setColor(label, e.target.value)}
                />
              </span>
              {/* The chip body is a toggle: click to hide / show every node with
                  this label. */}
              <button
                className="legend-toggle"
                aria-pressed={!off}
                title={off ? `Show “${label}” nodes` : `Hide “${label}” nodes`}
                onClick={() => toggleHidden(label)}
              >
                {label} {n}
              </button>
              {overridden && (
                <button
                  className="legend-reset"
                  title={`Reset “${label}” to its automatic colour`}
                  aria-label={`Reset colour for ${label}`}
                  onClick={() => clearColor(label)}
                >
                  <RotateCcw size={10} />
                </button>
              )}
            </span>
          )
        })}
      </div>
    </div>
  )
}
